import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

import { useErrorHandler } from '@shared/hooks/useErrorHandler';
import type { PermissionGroupDto } from '@shared/services/apiService';

import type { AccessControlApi } from '../model/useAccessControlApi';
import { useStyles } from './styles';

interface DeleteGroupDialogProps {
  group: PermissionGroupDto | null;
  memberCount?: number;
  api: AccessControlApi;
  onClose: () => void;
}

export const DeleteGroupDialog: React.FC<DeleteGroupDialogProps> = ({
  group,
  memberCount,
  api,
  onClose,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { showError } = useErrorHandler();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!group) return;
    try {
      setIsDeleting(true);
      await api.deleteGroup(group.id);
      onClose();
    } catch (error) {
      showError({ title: t('accessControl.groups.errors.deleteTitle'), error });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog
      open={Boolean(group)}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: classes.modalPaper }}>
      <DialogTitle>{t('accessControl.groups.delete.title')}</DialogTitle>
      <DialogContent dividers className={classes.modalContent}>
        <Typography>
          {t('accessControl.groups.delete.confirmation', { name: group?.name })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {memberCount
            ? t('accessControl.groups.delete.membersWarning', { count: memberCount })
            : t('accessControl.groups.delete.warning')}
        </Typography>
      </DialogContent>
      <DialogActions className={classes.modalActions}>
        <Button onClick={onClose} disabled={isDeleting}>
          {t('accessControl.common.cancel')}
        </Button>
        <Button variant="contained" color="error" onClick={handleDelete} disabled={isDeleting}>
          {isDeleting ? t('accessControl.common.deleting') : t('accessControl.common.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
